import { AUTH_ERROR_CODES, type AvailabilityConflict } from "@open-booking/shared";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { setCsrfToken } from "@/src/auth/store/auth-slice";
import { httpClient } from "@/src/common/services/http.service";

type EdenClient = typeof httpClient;
export type EdenCall<T> = (client: EdenClient) => Promise<{
	data: T | null;
	error: unknown;
	status: number;
	response: Response;
	headers: HeadersInit | undefined;
}>;

let refreshPromise: Promise<string | null> | null = null;
let csrfPromise: Promise<string | null> | null = null;

async function tryRefreshToken(): Promise<string | null> {
	try {
		const { data, error } = await httpClient.auth.refresh.post();
		if (error || !data) return null;
		return data.csrf_token;
	} catch {
		return null;
	}
}

async function tryFetchCsrf(): Promise<string | null> {
	try {
		const { data, error } = await httpClient.auth.csrf.get();
		if (error || !data) return null;
		return data.csrf_token;
	} catch {
		return null;
	}
}

async function executeCall(edenCall: EdenCall<unknown>) {
	try {
		const { data, error, status } = await edenCall(httpClient);

		if (error) {
			const body = typeof error === "object" && error !== null && "value" in error ? (error as Record<string, unknown>).value : error;
			return { error: { status, data: body } };
		}

		return { data };
	} catch (e) {
		return {
			error: {
				status: "CUSTOM_ERROR" as const,
				error: e instanceof Error ? e.message : String(e),
			},
		};
	}
}

function isCsrfError(errorData: unknown): boolean {
	if (typeof errorData !== "object" || errorData === null || !("error" in errorData)) return false;
	const code = (errorData as Record<string, unknown>).error;
	return code === AUTH_ERROR_CODES.CSRF_TOKEN_INVALID || code === AUTH_ERROR_CODES.CSRF_TOKEN_MISSING;
}

export function extractErrorCode(e: unknown): string | null {
	if (!e || typeof e !== "object" || !("data" in e)) return null;
	const data = (e as { data?: unknown }).data;
	if (!data || typeof data !== "object" || !("error" in data)) return null;
	const code = (data as { error?: unknown }).error;
	return typeof code === "string" ? code : null;
}

export function extractErrorConflicts(e: unknown): AvailabilityConflict[] {
	if (!e || typeof e !== "object" || !("data" in e)) return [];
	const data = (e as { data?: unknown }).data;
	if (!data || typeof data !== "object" || !("conflicts" in data)) return [];
	const conflicts = (data as { conflicts?: unknown }).conflicts;
	return Array.isArray(conflicts) ? (conflicts as AvailabilityConflict[]) : [];
}

export const edenBaseQuery = (): BaseQueryFn<EdenCall<unknown>> => async (edenCall, api) => {
	let result = await executeCall(edenCall);

	if (result.error && typeof result.error.status === "number" && result.error.status === 401) {
		if (!refreshPromise) {
			refreshPromise = tryRefreshToken().finally(() => {
				refreshPromise = null;
			});
		}
		const csrf = await refreshPromise;

		if (csrf) {
			api.dispatch(setCsrfToken(csrf));
			result = await executeCall(edenCall);
		} else {
			const { performLogout } = await import("@/src/auth/services/logout.service");
			await performLogout();
			return result;
		}
	}

	if (
		result.error &&
		typeof result.error.status === "number" &&
		result.error.status === 403 &&
		"data" in result.error &&
		isCsrfError(result.error.data)
	) {
		if (!csrfPromise) {
			csrfPromise = tryFetchCsrf().finally(() => {
				csrfPromise = null;
			});
		}
		const csrf = await csrfPromise;
		if (csrf) {
			api.dispatch(setCsrfToken(csrf));
			result = await executeCall(edenCall);
		}
	}

	return result;
};
